/**
 * Eén richting: "een melding vraagt het scherm" → de stand-bylaag reageert.
 *
 * Spec: specs/20260831-domotica-meldingen-bewonerstablet/plan.md §6.5
 * Taak: T-2.07
 *
 * ── WAAROM EEN BRUG EN GEEN DIRECTE AANROEP ─────────────────────────────────
 *
 * `NotificationStore` draait op moduleniveau en kent React niet; `ScreensaverController`
 * en de slaapstand leven in de React-boom en houden hun toestand in hooks. De store kan
 * die dus niet aanroepen, en de meldingcomponenten mógen de screensaver niet kennen —
 * anders krijg je een wederzijdse afhankelijkheid waarin de screensaver op zijn beurt gaat
 * beslissen welke melding mag verschijnen.
 *
 * Deze brug is bewust dom: hij draagt een verzoek, geen beleid. Wie luistert, beslist.
 *
 * ── DE GRENS LIGT BIJ `urgent`, EN DAT IS EEN BESLISSING ────────────────────
 *
 * (OQ-10, vastgelegd 2026-08-31 — strenger dan het oorspronkelijke voorstel.)
 *
 *   `low`     — alleen de lijst. Wekt niets, opent niets.
 *   `normal`  — toont volgens `presentation`. Zichtbaar dóór de dimlaag, maar
 *               onderbreekt de screensaver niet en wekt niet uit de slaapstand.
 *   `high`    — onderbreekt dim én screensaver. **Wekt NIET uit de slaapstand.**
 *   `urgent`  — alles hierboven, plus wakker maken en naar de voorgrond halen.
 *
 * Een tablet die 's nachts wakker wordt van een deurcontact is een klacht in wording. Door
 * de grens bij `urgent` te leggen moet een automation-auteur die keuze bewust maken in
 * plaats van hem per ongeluk te erven van een prioriteit die "belangrijk" heet.
 *
 * ── DIM EN SCREENSAVER WORDEN NIET "UITGEZET" ───────────────────────────────
 *
 * Dat hoeft niet: elke meldingslaag zit in `constants/zIndex.ts` bóven de stand-byband, dus
 * de melding is per definitie zichtbaar door de dimlaag en over de screensaver heen. Wat
 * deze brug toevoegt is dat de screensaver bij `high` en hoger ook daadwerkelijk WEGGAAT
 * in plaats van erachter te blijven staan — anders zit de bewoner na het wegklikken van de
 * melding meteen weer tegen een fotoscherm aan.
 */

import type { ResidentNotificationPriority } from '@caro/shared/types';

/** Wat er gevraagd wordt. Nooit meer dan de prioriteit toestaat. */
export interface AttentionRequest {
  /** De screensaver mag weg (dim is al opgelost door de stapelvolgorde). */
  breakScreensaver: boolean;
  /** De slaapstand mag verbroken worden. Uitsluitend bij `urgent`. */
  wakeFromSleep: boolean;
  /** De app naar de voorgrond halen (native). Uitsluitend bij `urgent`. */
  bringToFront: boolean;
  /** Alleen ter documentatie in de log — de brug beslist er niets mee. */
  priority: ResidentNotificationPriority;
}

/**
 * Vertaal een prioriteit naar wat hij mag.
 *
 * ⚠ Dit is de ENIGE plek waar die tabel staat. Een tweede formulering elders loopt vroeg of
 * laat uit de pas, en dan is de vraag "wekt `high` de tablet?" niet meer met één blik te
 * beantwoorden.
 */
export function attentionFor(priority: ResidentNotificationPriority): AttentionRequest {
  return {
    breakScreensaver: priority === 'high' || priority === 'urgent',
    wakeFromSleep: priority === 'urgent',
    bringToFront: priority === 'urgent',
    priority,
  };
}

/** Mag een melding met deze prioriteit überhaupt het scherm claimen? */
export function claimsScreen(priority: ResidentNotificationPriority): boolean {
  return priority !== 'low';
}

type Listener = (request: AttentionRequest) => void;

const listeners = new Set<Listener>();

/**
 * Meldt zich als afhandelaar. `ScreensaverController` is vandaag de enige.
 *
 * Meerdere luisteraars zijn toegestaan en krijgen allemaal hetzelfde verzoek; een
 * gooiende luisteraar mag de rest niet meenemen.
 */
export function onAttentionRequest(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Vraag aandacht voor een melding van deze prioriteit.
 *
 * Werpt nooit, en doet niets wanneer er niemand luistert — een melding mag nooit
 * mislukken omdat de stand-bylaag toevallig niet gemonteerd is.
 */
export function requestAttention(priority: ResidentNotificationPriority): void {
  const request = attentionFor(priority);
  if (!request.breakScreensaver && !request.wakeFromSleep && !request.bringToFront) return;
  listeners.forEach((l) => {
    try {
      l(request);
    } catch {
      /* één afhandelaar mag de andere niet slopen */
    }
  });
}

/** Alleen voor tests. */
export function __resetAttentionBridge(): void {
  listeners.clear();
}
